import { ModuleApiProvider, useAuth, type ModuleReference } from '@berky/auth'
import type { ModuleConfig } from '@berky/module-contract'
import { LoadingSkeleton } from '@berky/ui'
import { createElement, lazy, Suspense, type ComponentType } from 'react'
import { Navigate, useRoutes } from 'react-router'

import { ModuleErrorBoundary } from './module-error-boundary'
import { moduleAccess } from '../module-access'

/**
 * Pro Modul genau eine Lazy-Komponente, die dessen Routen rendert.
 *
 * Warum `lazy()` und nicht `use()`: `lazy` ist der Weg, den React für
 * nachgeladenen Code vorsieht — er löst auch dann zuverlässig auf, wenn gerade
 * kein anderes Ereignis den Baum aktualisiert. Mit `use()` blieb in Tests der
 * Suspense-Fallback stehen, bis zufällig eine andere Aktualisierung kam.
 */
const moduleComponents = new Map<string, ComponentType>()

function getModuleRoutesComponent(module: ModuleConfig): ComponentType {
  let component = moduleComponents.get(module.id)
  if (!component) {
    component = lazy(async () => {
      const loaded = await module.routes()
      return {
        default: function ModuleRoutes() {
          return useRoutes(loaded.default)
        },
      }
    })
    moduleComponents.set(module.id, component)
  }
  return component
}

/**
 * Alles, was um ein Modul herum gilt: Rollenprüfung, API-Kontext,
 * Fehlerisolierung und Ladeanzeige. Ein Modul bringt davon nichts selbst mit.
 */
export function ModuleRoute({
  module,
  availableModules = [],
}: {
  module: ModuleConfig
  availableModules?: readonly ModuleReference[]
}) {
  const { user, isLoading } = useAuth()
  const access = moduleAccess(user?.roles, module)

  // `unbekannt` getrennt von `verborgen` behandeln: Solange die Rollen nicht da
  // sind, darf hier nichts umgeleitet werden. Ein `<Navigate replace>` schreibt
  // die Adresse sofort um — MSAL merkt sich dann `/forbidden` als Rücksprungziel
  // und bringt einen nach der Anmeldung genau dorthin zurück.
  if (isLoading || access === 'unbekannt') {
    return <LoadingSkeleton variant="text" rows={4} />
  }

  if (access !== 'erlaubt') {
    return (
      <Navigate
        to="/forbidden"
        replace
        state={{ moduleId: module.id, moduleTitle: module.title }}
      />
    )
  }

  return (
    <ModuleApiProvider
      module={{
        id: module.id,
        apiBasePath: module.apiBasePath,
        availableModules,
      }}
    >
      <ModuleErrorBoundary moduleId={module.id} moduleTitle={module.title}>
        <Suspense fallback={<LoadingSkeleton variant="text" rows={6} />}>
          {/* createElement statt JSX: die Komponente kommt aus dem Cache oben und
              ist über Renders hinweg stabil. Als lokale Variable im JSX würde sie
              wie eine im Render erzeugte Komponente aussehen. */}
          {createElement(getModuleRoutesComponent(module))}
        </Suspense>
      </ModuleErrorBoundary>
    </ModuleApiProvider>
  )
}
